"use client"

import React, { useMemo, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { ChevronRight, Loader2 } from "lucide-react"
import { isColumnField, isExpandableNode } from "@/lib/ats-schema-expansion"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import type { AtsFieldDescriptor } from "@/types/ats-explorer-api"

export interface SchemaFieldListProps {
  fields: AtsFieldDescriptor[] | null
  isLoading: boolean
  /** Selected keys — `keyFor(path)` values, so composite pulls can store prefixed keys */
  selected: Set<string>
  onToggle: (path: string, checked: boolean) => void
  /** Maps a field path to the key stored in `selected` (identity by default) */
  keyFor?: (path: string) => string
  expandedPaths: Set<string>
  expandingPath: string | null
  onExpand: (nodePath: string) => void
  /** Bulk actions rendered above the list */
  onSelectDefaults?: () => void
  onSelectAllColumns?: () => void
  onClear?: () => void
  /** Extra controls rendered at the end of an association node's row (a to-many layout chooser) */
  nodeExtra?: (field: AtsFieldDescriptor) => React.ReactNode
  className?: string
}

/**
 * Checkbox list over a discovered schema. Association nodes ("jobOrder ▸ JobOrder")
 * render as expanders rather than checkboxes; expanding one inlines the associated
 * entity's fields under it. Custom fields fold away behind a toggle, and a search box
 * narrows long lists (Bullhorn entities carry hundreds of paths).
 */
export function SchemaFieldList({
  fields,
  isLoading,
  selected,
  onToggle,
  keyFor = (path) => path,
  expandedPaths,
  expandingPath,
  onExpand,
  onSelectDefaults,
  onSelectAllColumns,
  onClear,
  nodeExtra,
  className,
}: SchemaFieldListProps) {
  const { t, n } = useTranslation()
  const [search, setSearch] = useState("")
  const [showCustom, setShowCustom] = useState(false)

  const hasCustomFields = useMemo(() => fields?.some((field) => field.isCustom) ?? false, [fields])

  const visibleFields = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (fields ?? []).filter(
      (field) =>
        (showCustom || !field.isCustom || selected.has(keyFor(field.path))) &&
        (term === "" || field.path.toLowerCase().includes(term) || (field.label?.toLowerCase().includes(term) ?? false))
    )
  }, [fields, search, showCustom, selected, keyFor])

  const selectedCount = useMemo(() => (fields ?? []).filter((field) => selected.has(keyFor(field.path))).length, [fields, selected, keyFor])

  if (isLoading && fields === null) {
    return (
      <div className="flex items-center py-4 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> {t("explorer.schemaFields.discovering")}
      </div>
    )
  }

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {onSelectDefaults && (
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onSelectDefaults}>
            {t("explorer.schemaFields.defaults")}
          </Button>
        )}
        {onSelectAllColumns && (
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onSelectAllColumns}>
            {t("explorer.schemaFields.allColumns")}
          </Button>
        )}
        {onClear && (
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onClear}>
            {t("explorer.common.clear")}
          </Button>
        )}
        <Input
          className="h-7 text-xs w-48"
          placeholder={t("explorer.schemaFields.searchFields")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {hasCustomFields && (
          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
            <Checkbox checked={showCustom} onCheckedChange={(checked) => setShowCustom(checked === true)} className="h-3.5 w-3.5" />
            {t("explorer.schemaFields.showCustom")}
          </label>
        )}
        <span className="text-xs text-muted-foreground ml-auto">
          {t("explorer.schemaFields.selectedOfTotal", { selected: n(selectedCount), total: n(fields?.length ?? 0) })}
        </span>
      </div>

      <div className="border rounded-md p-2 max-h-80 overflow-y-auto">
        {visibleFields.length === 0 && <p className="text-xs text-muted-foreground p-2">{t("explorer.schemaFields.noMatch")}</p>}
        {visibleFields.map((field) =>
          isExpandableNode(field) ? (
            <ExpandRow
              key={field.path}
              field={field}
              isExpanded={expandedPaths.has(field.path)}
              isExpanding={expandingPath === field.path}
              onExpand={() => onExpand(field.path)}
              extra={nodeExtra?.(field)}
            />
          ) : (
            <label key={field.path} className="flex items-center gap-2 py-0.5 text-sm cursor-pointer">
              <Checkbox
                checked={selected.has(keyFor(field.path))}
                onCheckedChange={(checked) => onToggle(field.path, checked === true)}
                className="h-3.5 w-3.5"
              />
              <code className="text-xs">{field.path}</code>
              <Badge variant="outline" className="text-[10px] px-1 py-0">
                {field.dataType}
              </Badge>
              {field.label && field.label !== field.path && <span className="text-xs text-muted-foreground truncate max-w-48">{field.label}</span>}
              {!isColumnField(field) && <span className="text-[10px] text-muted-foreground">{t("explorer.schemaFields.list")}</span>}
            </label>
          )
        )}
      </div>
    </div>
  )
}

interface ExpandRowProps {
  field: AtsFieldDescriptor
  isExpanded: boolean
  isExpanding: boolean
  onExpand: () => void
  extra?: React.ReactNode
}

function ExpandRow({ field, isExpanded, isExpanding, onExpand, extra }: ExpandRowProps) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-2 py-0.5 text-sm">
      <button
        type="button"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-60"
        onClick={onExpand}
        disabled={isExpanded || isExpanding}
        title={isExpanded ? t("explorer.schemaFields.expanded") : t("explorer.schemaFields.expandTitle", { entity: field.associatedEntityType ?? "", path: field.path })}
      >
        {isExpanding ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <ChevronRight className={`h-3.5 w-3.5 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
        )}
        <code className="text-xs text-foreground">{field.path}</code>
      </button>
      <Badge variant="secondary" className="text-[10px] px-1 py-0">
        {field.associatedEntityType}
      </Badge>
      {!isExpanded && <span className="text-[10px] text-muted-foreground">{t("explorer.schemaFields.expandHint")}</span>}
      {extra}
    </div>
  )
}
